<template>
  <div class="game-mobile-container">
    <!-- 操作按钮区域，固定在右下角 -->
    <div class="mobile-action-buttons">
      <!-- 查询按钮 -->
      <el-button class="mobile-icon-btn" @click="showFilterDialog">
        <i class="el-icon-search"></i>
      </el-button>
      
      <!-- 统计按钮 -->
      <el-button class="mobile-icon-btn" @click="showStatisticsDialog">
        <i class="el-icon-data-analysis"></i>
      </el-button>
    </div>
    
    <!-- 标题栏 -->
    <div class="mobile-header" :class="{ 'header-hidden': !headerVisible }">
      <h1 class="page-title">赛事列表</h1>
    </div>
    
    <!-- 赛事卡片区域 -->
    <div class="game-cards-container" :class="{ 'header-hidden': !headerVisible }" v-loading="loading" @scroll="handleScroll">
      <div v-if="gameList.length === 0 && !loading" class="no-data">
        <div class="no-data-content">
          <i class="el-icon-document"></i>
          <span class="no-data-title">暂无赛事</span>
          <p class="no-data-tips">
            没有找到符合条件的赛事，请尝试调整筛选条件
          </p>
        </div>
      </div>
      
      <template v-else>
        <div 
          v-for="game in gameList" 
          :key="game.game_code"
          class="game-card"
          :class="{ 'game-card-expanded': expandedGameCode === game.game_code }"
          @click="handleCardClick(game)"
        >
          <!-- 难度指示器（右上角三角形） -->
          <div class="difficulty-indicator">
            <div class="triangle-background"
                 :style="{ 
                   'border-color': game.avg_difficulty !== null ? 
                     `transparent ${getDifficultyColorByPercentage(game.avg_difficulty, 0.85)} transparent transparent` : 
                     'transparent rgba(144, 147, 153, 0.85) transparent transparent'
                 }">
            </div>
            <span v-if="game.avg_difficulty !== null" class="difficulty-value">
              {{ game.avg_difficulty > 0.1 ? formatDifficultyPercentage(game.avg_difficulty) : '未定义' }}
            </span>
            <span v-else class="difficulty-value">未定义</span>
          </div>
          
          <!-- 订正水印 -->
          <div v-if="game.fix_status === '已完成'" class="fix-stamp">
            <div class="stamp-content" :class="{ 'ak-stamp': game.total_score === game.question_num * 100 }">
              {{ game.total_score === game.question_num * 100 ? 'AK' : '订' }}
            </div>
          </div>
          
          <!-- 赛事基本信息 -->
          <div class="game-card-main">
            <!-- 赛事名称区域 -->
            <div class="game-header">
              <h3 class="game-name">
                <!-- 录像机图标 - 表示有复盘内容 -->
                <i v-if="game.has_replay" class="el-icon-video-camera replay-icon" @click.stop="showReplay(game)"></i>
                <!-- 赛事级别标签 -->
                <span v-if="game.game_level" class="game-level-tag" :style="getGameLevelStyle(game.game_level)">
                  {{ game.game_level }}
                </span>
                {{ game.game_name }}
              </h3>
            </div>
            
            <div class="game-info">
              <div class="game-type-date">
                <span class="flag-icon">🏁</span>
                {{ game.game_type || '未设置' }}@{{ formatDate(game.game_time) }}
              </div>
              
              <div class="game-data-grid">
                <div class="game-score">
                  <span class="score-label">得分：</span>
                  <div class="score-value-container">
                    <!-- 原始分数 -->
                    <span class="score-value" :style="{color: getScoreColorByPercentage(game.score_rate)}">
                      {{ formatNumber(game.total_score) }}
                    </span>
                    <span class="score-separator">/</span>
                    <!-- 订正分数 -->
                    <span class="score-value" :style="{color: getScoreColorByPercentage(getFixScoreRate(game))}">
                      {{ formatNumber(game.total_score_fix || game.total_score) }}
                    </span>
                  </div>
                </div>
                <div class="game-rank">
                  <span class="rank-label">排名：</span>
                  <div class="rank-value-container">
                    <span class="rank-value">
                      {{ formatNumber(game.rank || 0) }}
                    </span>
                    <span class="rank-separator">/</span>
                    <span class="rank-value">
                      {{ formatNumber(game.parameter_number || 0) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      
      <!-- 加载更多 -->
      <div v-if="hasMore && !loading && gameList.length > 0" class="load-more">
        <el-button type="text" @click="loadMore">加载更多</el-button>
      </div>
      <div v-if="loading && gameList.length > 0" class="loading-more">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-if="!hasMore && gameList.length > 0" class="no-more">
        <span>------已经到底啦------</span>
        <div class="copyright-info">
          <span class="mobile-copyright">Copyright © 2024 OIer之路 All rights reserved.<br>闽ICP备2025095302号-1</span>
        </div>
      </div>
    </div>
    
    <!-- 自定义统计信息弹窗 -->
    <div class="dialog-container" v-show="statisticsDialogVisible">
      <div class="dialog-mask" @click="statisticsDialogVisible = false"></div>
      <div class="custom-dialog stats-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="statisticsDialogVisible = false">×</div>
        <div class="dialog-body statistics-dialog-body">
          <div class="dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title">赛事统计</h3>
            <div class="mobile-statistics-grid">
              <!-- 场次/题数 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.total_games) }} / {{ formatNumber(statistics.total_questions) }}</div>
                <div class="stat-label">
                  <i class="el-icon-trophy"></i>
                  场次/题数
                </div>
              </div>
              
              <!-- 得分率 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatPercentage(statistics.score_rate) }}</div>
                <div class="stat-label">
                  <i class="el-icon-data-line"></i>
                  得分率
                </div>
              </div>
              
              <!-- 排位 -->
              <div class="stat-item">
                <div class="stat-value">Top {{ formatPercentage(statistics.rank_rate) }}</div>
                <div class="stat-label">
                  <i class="el-icon-sort"></i>
                  排位
                </div>
              </div>
              
              <!-- 平均难度 -->
              <div class="stat-item">
                <div class="stat-value difficulty-value">
                  <template v-if="statistics.avg_difficulty !== null && statistics.avg_difficulty !== undefined">
                    <span :style="{ color: getDifficultyTextColor(statistics.avg_difficulty) }">
                      <span 
                        class="difficulty-color-block" 
                        :style="{ backgroundColor: getDifficultyBlockColor(statistics.avg_difficulty) }">
                      </span>
                      {{ formatDifficultyPercentage(statistics.avg_difficulty) }}
                    </span>
                  </template>
                  <template v-else>未定义</template>
                </div>
                <div class="stat-label">
                  <i class="el-icon-finished"></i>
                  平均难度
                </div>
              </div>
              
              <!-- 未订正 -->
              <div class="stat-item">
                <div class="stat-value unfix-value">{{ formatNumber(statistics.unfix_count) }}</div>
                <div class="stat-label">
                  <i class="el-icon-bell"></i>
                  未订正
                </div>
              </div>
              
              <!-- 挂起 -->
              <div class="stat-item">
                <div class="stat-value pending-value">{{ formatNumber(statistics.pending_count) }}</div>
                <div class="stat-label">
                  <i class="el-icon-video-pause"></i>
                  挂起
                </div>
              </div>
              
              <!-- AC率 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatPercentage(statistics.ac_rate) }}</div>
                <div class="stat-label">
                  <i class="el-icon-circle-check"></i>
                  AC率
                </div>
              </div>
              
              <!-- 订正率 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatPercentage(statistics.fix_rate) }}</div>
                <div class="stat-label">
                  <i class="el-icon-notebook-2"></i>
                  订正率
                </div>
              </div>
              
              <!-- 掌握率 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatPercentage(statistics.master_rate) }}</div>
                <div class="stat-label">
                  <i class="el-icon-magic-stick"></i>
                  掌握率
                </div>
              </div>
              
              <!-- 复盘率 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatPercentage(statistics.replay_rate) }}</div>
                <div class="stat-label">
                  <i class="el-icon-video-camera"></i>
                  复盘率
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 自定义查询筛选弹窗 -->
    <div class="dialog-container" v-show="filterDialogVisible">
      <div class="dialog-mask" @click="filterDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="filterDialogVisible = false">×</div>
        <div class="dialog-body filter-dialog-body">
          <div class="dialog-content filter-dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title filter-title">查询条件</h3>
            <el-form :model="filterForm" class="filter-form" size="small" label-width="70px">
              <el-form-item label="赛事名称">
                <el-input v-model="filterForm.game_name" placeholder="请输入赛事名称"></el-input>
              </el-form-item>
              
              <el-form-item label="赛事类型">
                <el-select v-model="filterForm.game_type" placeholder="请选择赛事类型" clearable style="width: 100%">
                  <el-option
                    v-for="item in dictData.game_type"
                    :key="item.dict_value"
                    :label="item.dict_value"
                    :value="item.dict_value">
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="赛事级别">
                <el-select v-model="filterForm.game_level" placeholder="请选择赛事级别" clearable style="width: 100%">
                  <el-option
                    v-for="item in dictData.game_level"
                    :key="item.dict_value"
                    :label="item.dict_value"
                    :value="item.dict_value">
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="订正状态">
                <el-select v-model="filterForm.fix_status" placeholder="请选择订正状态" clearable style="width: 100%">
                  <el-option label="已完成" value="已完成"></el-option>
                  <el-option label="未完成" value="未完成"></el-option>
                  <el-option label="未添加" value="未添加"></el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="复盘状态">
                <el-select v-model="filterForm.replay_status" placeholder="请选择复盘状态" clearable style="width: 100%">
                  <el-option label="已完成" value="已完成"></el-option>
                  <el-option label="未完成" value="未完成"></el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="比赛日期">
                <div class="date-range-container">
                  <el-date-picker
                    v-model="filterForm.start_date"
                    type="date"
                    placeholder="开始日期"
                    value-format="yyyy-MM-dd"
                    size="small"
                    style="width: 100%;"
                    class="date-picker-item">
                  </el-date-picker>
                  <span class="date-separator">至</span>
                  <el-date-picker
                    v-model="filterForm.end_date"
                    type="date"
                    placeholder="结束日期"
                    value-format="yyyy-MM-dd"
                    size="small"
                    style="width: 100%;"
                    class="date-picker-item">
                  </el-date-picker>
                </div>
              </el-form-item>
            </el-form>
            <div class="dialog-footer filter-footer">
              <el-button size="small" @click="handleReset">重置</el-button>
              <el-button size="small" type="primary" @click="handleSearch">查询</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 赛事详情弹窗 - 完全简化版 -->
    <div class="dialog-container" v-show="detailDialogVisible">
      <div class="dialog-mask" @click="detailDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="detailDialogVisible = false">×</div>
        <div class="dialog-body">
          <div v-if="currentGame" class="game-detail detail-scroll-container" style="background-color: #ffffff;">
            <div class="detail-header">
              <h3>
                <!-- 赛事名称 - 如果有URL则显示为可点击链接 -->
                <a v-if="currentGame.game_url" 
                   :href="currentGame.game_url" 
                   target="_blank"
                   class="game-name-link">
                  {{ currentGame.game_name }}
                </a>
                <span v-else>{{ currentGame.game_name }}</span>
              </h3>
              
              <!-- 详情页的订正水印/印章 -->
              <div v-if="currentGame.fix_status === '已完成'" class="detail-fix-stamp">
                <div class="stamp-content" :class="{ 'ak-stamp': currentGame.total_score === currentGame.question_num * 100 }">
                  {{ currentGame.total_score === currentGame.question_num * 100 ? 'AK' : '订' }}
                </div>
              </div>
            </div>
            
            <div class="detail-content">
              <div class="detail-item">
                <div class="detail-label">赛事类型</div>
                <div class="detail-value">{{ currentGame.game_type || '未设置' }}</div>
              </div>
              
              <div class="detail-item">
                <div class="detail-label">赛事时间</div>
                <div class="detail-value">{{ formatDate(currentGame.game_time) }}</div>
              </div>
              
              <div class="detail-item">
                <div class="detail-label">分数</div>
                <div class="detail-value score-value-container">
                  <!-- 原始分数 -->
                  <span :style="{color: getScoreColorByPercentage(currentGame.score_rate)}">
                    {{ formatNumber(currentGame.total_score || 0) }}
                  </span>
                  <span class="score-separator">/</span>
                  <!-- 订正分数 -->
                  <span :style="{color: getScoreColorByPercentage(getFixScoreRate(currentGame))}">
                    {{ formatNumber(currentGame.total_score_fix || currentGame.total_score || 0) }}
                  </span>
                </div>
              </div>
              
              <div class="detail-item">
                <div class="detail-label">排名</div>
                <div class="detail-value rank-value-container">
                  <span>{{ formatNumber(currentGame.rank || 0) }}</span>
                  <span class="rank-separator">/</span>
                  <span>{{ formatNumber(currentGame.parameter_number || 0) }}</span>
                </div>
              </div>
              
              <!-- 难度显示为彩色标签，与卡片中的风格保持一致 -->
              <div class="detail-item">
                <div class="detail-label">难度</div>
                <div class="detail-value">
                  <span v-if="currentGame.avg_difficulty !== null" class="difficulty-badge"
                       :style="{
                         backgroundColor: getDifficultyColorByPercentage(currentGame.avg_difficulty)
                       }">
                    {{ formatDifficultyPercentage(currentGame.avg_difficulty) }}
                  </span>
                  <span v-else class="difficulty-badge no-difficulty">
                    未定义
                  </span>
                </div>
              </div>
              
              <div class="detail-section">
                <h4>题目信息</h4>
                <div v-if="gameQuestions.length > 0" class="question-list">
                  <div v-for="(question, index) in gameQuestions" :key="question.question_code" class="question-item">
                    <div class="question-header">
                      <!-- 题号：背景色使用题目难度对应的颜色，仅显示题号数字 -->
                      <span class="question-seq" :style="getQuestionLevelStyle(question.difficulty)">{{ question.question_seq }}</span>
                      <!-- 题目名称 - 如果有URL则显示为可点击链接 -->
                      <span class="question-name">
                        <a v-if="question.question_url" 
                           :href="question.question_url" 
                           target="_blank"
                           class="question-name-link">
                          {{ question.question_name }}
                        </a>
                        <span v-else>{{ question.question_name }}</span>
                      </span>
                      <!-- 操作切换按钮与滑出面板 -->
                      <div class="question-actions-inline">
                        <el-button 
                          class="question-action-btn action-toggle-btn" 
                          @click.stop="toggleQuestionActions(question)"
                          title="操作">
                          <i class="el-icon-setting"></i>
                        </el-button>
                        <div class="actions-slide-panel" :class="{ open: isQuestionActionsOpen(question) }">
                          <el-button 
                            class="question-action-btn" 
                            :disabled="question.question_score_fix === 100"
                            @click.stop="handleQuestionFix(question)"
                            title="订正">
                            <i class="el-icon-check" :style="{color: question.question_score_fix === 100 ? '#C0C4CC' : '#409EFF'}"></i>
                          </el-button>
                          <el-button 
                            class="question-action-btn" 
                            @click.stop="handleQuestionCollect(question)"
                            :title="question.is_collect ? '取消收藏' : '收藏'">
                            <i :class="{'el-icon-star-off': !question.is_collect, 'el-icon-star-on': question.is_collect}"
                               :style="{
                                 color: question.is_collect ? '#F7BA2A' : '#409EFF',
                                 fontSize: question.is_collect ? '16px' : '14px'
                               }">
                            </i>
                          </el-button>
                          <el-button 
                            class="question-action-btn" 
                            @click.stop="handleQuestionPending(question)"
                            :title="question.is_pending ? '取消挂起' : '挂起'">
                            <i class="el-icon-video-pause"
                               :style="{
                                 color: question.is_pending ? '#E6A23C' : '#409EFF',
                                 fontSize: question.is_pending ? '16px' : '14px'
                               }">
                            </i>
                          </el-button>
                        </div>
                      </div>
                    </div>
                    <div class="question-info">
                      <!-- 得分 - 只对数字应用颜色 -->
                      <div class="score-cell">
                        <span class="score-label">得分：</span>
                        <i v-if="question.question_score === 100" 
                           class="el-icon-medal-1" 
                           :style="{ color: getScoreColor(question.question_score), marginRight: '4px' }"></i>
                        <span class="score-value" :style="{ 
                          color: getScoreColor(question.question_score),
                          fontWeight: 'bold'
                        }">
                          {{ question.question_score || 0 }}
                        </span>
                      </div>
                      
                      <!-- 订正分数 - 只对数字应用颜色 -->
                      <div class="score-cell">
                        <span class="score-label">订正：</span>
                        <i v-if="question.question_score_fix === 100" 
                           class="el-icon-medal-1" 
                           :style="{ color: getScoreColor(question.question_score_fix), marginRight: '4px' }"></i>
                        <span class="score-value" :style="{ 
                          color: getScoreColor(question.question_score_fix),
                          fontWeight: 'bold'
                        }">
                          {{ question.question_score_fix || 0 }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- 错误类型（描边/空心样式，参考 QuestionMobile.vue） -->
                    <div v-if="normalizeErrors(question.errors).length > 0" class="question-errors">
                      <el-tag
                        v-for="err in normalizeErrors(question.errors)"
                        :key="(err.error_code || err.error_name) + '-gm'"
                        :style="getErrorOutlineStyle(err.error_code || err.error_name)"
                        size="mini"
                        style="margin-right: 4px; margin-bottom: 4px;">
                        <i class="el-icon-warning" style="margin-right: 3px;"></i>{{ err.error_name || err }}
                      </el-tag>
                    </div>
                    
                    <!-- 标签 - 参考QuestionList.vue样式 -->
                    <div class="question-tags">
                      <el-tag
                        v-for="tag in question.tags"
                        :key="tag.tag_code"
                        size="mini"
                        :class="getTagColorClass(tag.tag_code)"
                        class="question-tag"
                      >{{ tag.tag_name }}</el-tag>
                    </div>
                    
                    
                  </div>
                </div>
                <div v-else class="no-questions">
                  <i class="el-icon-document"></i>
                  <span>暂无题目信息</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 赛事复盘弹窗 -->
    <game-replay-mobile-view 
      :visible.sync="replayDialogVisible"
      :gameInfo="currentGame || {}"
      v-if="replayDialogVisible"
    />
  </div>
</template>

<script>
import { getGameList, getGameStatistics } from '@/api/game'
import { getQuestions, updateQuestionCollect } from '@/api/question'
import { getDictData } from '@/api/dict'
import request from '@/utils/request'
import { QUESTION_LEVELS, getQuestionLevelStyle as getOriginalQuestionLevelStyle } from '@/constants/questionLevel'
import { getErrorOutlineStyle } from '@/constants/errorTypes'
import GameReplayMobileView from './components/GameReplayMobileView.vue'

export default {
  name: 'GameMobile',
  components: {
    GameReplayMobileView
  },
  data() {
    return {
      loading: false,
      gameList: [], // 当前页显示的数据
      currentPage: 1,
      pageSize: 20,
      total: 0,
      hasMore: true,
      sortProp: 'game_time',  // 默认按赛事时间排序
      sortOrder: 'descending',  // 默认倒序
      
      // 统计数据
      statistics: {
        total_games: 0,
        total_questions: 0,
        games_with_questions: 0,
        games_fixed: 0,
        games_replayed: 0,
        avg_score_rate: 0,
        avg_rank_rate: 0,
        score_rate: 0,
        rank_rate: 0,
        ac_rate: 0,
        fix_rate: 0,
        master_rate: 0,
        replay_rate: 0,
        unfix_count: 0,
        pending_count: 0,
        avg_difficulty: null
      },
      
      // 弹窗控制
      statisticsDialogVisible: false,
      filterDialogVisible: false,
      detailDialogVisible: false,
      replayDialogVisible: false,
      
      // 展开的卡片
      expandedGameCode: null,
      
      // 当前选中的赛事
      currentGame: null,
      gameQuestions: [],
      
      // 当前展开操作面板的题目编码（仅展开一个）
      openActionQuestionCode: null,
      
      // 查询表单
      filterForm: {
        game_name: '',
        game_type: '',
        game_level: '',
        fix_status: '',
        replay_status: '',
        start_date: '',
        end_date: '',
        game_time: []
      },
      
      // 字典数据
      dictData: {
        game_type: [],
        game_level: [],
        game_scale: [],
        game_org: []
      },

      // 滚动状态控制
      lastScrollTop: 0,
      headerVisible: true,
      scrollThreshold: 10 // 滚动阈值，避免微小滚动触发
    }
  },
  created() {
    this.loadDictData()
    this.fetchGameList()
    this.fetchStatistics()
  },
  
  mounted() {
    // 空的mounted钩子
    this.setupDatePickerStyles()
    // 点击空白处收起题目操作面板
    document.addEventListener('click', this.handleGlobalClick, true)
  },
  
  beforeDestroy() {
    // 清理MutationObserver
    if (this._pickerObserver) {
      this._pickerObserver.disconnect()
    }
    document.removeEventListener('click', this.handleGlobalClick, true)
  },
  methods: {
    // 错误类型相关
    normalizeErrors(errors) {
      if (!errors) return []
      if (Array.isArray(errors)) return errors
      try {
        const arr = JSON.parse(errors)
        return Array.isArray(arr) ? arr : []
      } catch (e) { return [] }
    },
    // 全局点击：若点击不在 actions 区域/切换按钮上，则收起
    handleGlobalClick(event) {
      const panel = event.target.closest && event.target.closest('.question-actions-inline')
      if (!panel) {
        this.openActionQuestionCode = null
      }
    },
    // 设置日期选择器样式
    setupDatePickerStyles() {
      // 创建一个MutationObserver来监听DOM变化
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length > 0) {
            // 查找所有日期选择器弹窗
            const pickers = document.querySelectorAll('.el-picker-panel')
            pickers.forEach(picker => {
              // 获取当前样式
              const style = window.getComputedStyle(picker)
              const currentTop = style.top
              
              // 只修改水平位置，保持垂直位置不变
              picker.style.left = '50%'
              picker.style.transform = 'translateX(-50%)'
              picker.style.margin = '0'
              picker.style.zIndex = '2500'
              
              // 确保不影响top值
              if (picker.style.top === '50%') {
                // 如果已经被我们的代码修改过，还原到默认值或自动计算值
                picker.style.top = ''
              }
              
              // 隐藏箭头
              const arrow = picker.querySelector('.popper__arrow')
              if (arrow) {
                arrow.style.display = 'none'
              }
            })
          }
        })
      })
      
      // 开始观察document.body的变化
      observer.observe(document.body, { childList: true, subtree: true })
      
      // 保存观察器引用，以便在组件销毁时断开连接
      this._pickerObserver = observer
    },
    
    // 加载字典数据
    async loadDictData() {
      try {
        const dictKeys = ['game_type', 'game_level', 'game_scale', 'game_org']
        const promises = dictKeys.map(key => getDictData(key))
        const results = await Promise.all(promises)
        
        results.forEach((res, index) => {
          if (res.code === 200) {
            this.dictData[dictKeys[index]] = res.data
          } else {
            console.error(`加载字典数据失败 (${dictKeys[index]}):`, res.message)
          }
        })
      } catch (error) {
        console.error('加载字典数据失败:', error)
        this.$message.error('加载字典数据失败')
      }
    },
    
    // 获取查询参数
    getQueryParams() {
      const params = {
        pageNum: this.currentPage,
        pageSize: this.pageSize,
        sort_prop: this.sortProp,
        sort_order: this.sortOrder
      };
      
      // 添加查询参数
      if (this.filterForm.game_name) {
        params.game_name = this.filterForm.game_name;
      }
      
      if (this.filterForm.game_type) {
        params.game_type = this.filterForm.game_type;
      }
      
      if (this.filterForm.game_level) {
        params.game_level = this.filterForm.game_level;
      }
      
      if (this.filterForm.fix_status) {
        params.fix_status = this.filterForm.fix_status;
      }
      
      if (this.filterForm.replay_status) {
        params.replay_status = this.filterForm.replay_status;
      }
      
      if (this.filterForm.game_time && this.filterForm.game_time.length === 2) {
        params.game_time_start = this.filterForm.game_time[0];
        params.game_time_end = this.filterForm.game_time[1];
      }
      
      return params;
    },
    
    // 获取赛事列表
    async fetchGameList(isLoadMore = false) {
      try {
        this.loading = true
        const params = this.getQueryParams()
        
        const res = await getGameList(params)
        if (res.code === 200) {
          if (isLoadMore) {
            this.gameList = [...this.gameList, ...res.data.items]
          } else {
            this.gameList = res.data.items || []
          }
          this.total = res.data.total
          this.hasMore = this.gameList.length < this.total
          
          // 调试数据结构
          this.debugGameData();
        } else {
          this.$message.error(res.msg || '获取赛事列表失败')
        }
      } catch (error) {
        console.error('获取赛事列表失败:', error)
        this.$message.error('获取赛事列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 调试赛事数据
    debugGameData() {
      if (this.gameList.length > 0) {
        const game = this.gameList[0];
        console.log('==== 赛事数据结构检查 ====');
        console.log('第一条赛事数据:', game);
        console.log('得分相关字段:', {
          total_score: game.total_score,
          total_score_fix: game.total_score_fix,
          question_num: game.question_num,
          full_score: game.question_num * 100,
          score_rate: game.score_rate,
          calculated_score_rate: game.total_score / (game.question_num * 100) * 100,
          calculated_fix_score_rate: (game.total_score_fix || game.total_score) / (game.question_num * 100) * 100
        });
        
        // 检查所有赛事是否都有total_score字段
        const missingScores = this.gameList.filter(g => g.total_score === undefined || g.total_score === null);
        if (missingScores.length > 0) {
          console.warn(`警告：有${missingScores.length}条赛事数据缺少total_score字段`);
        }
        
        // 检查分数计算结果
        const scoreFormula = this.formatNumber(game.total_score) + '/' + 
                           this.formatNumber(game.question_num * 100) + 
                           ' = ' + (game.total_score / (game.question_num * 100) * 100).toFixed(2) + '%';
        const fixScoreFormula = this.formatNumber(game.total_score_fix || game.total_score) + '/' + 
                               this.formatNumber(game.question_num * 100) + 
                               ' = ' + ((game.total_score_fix || game.total_score) / (game.question_num * 100) * 100).toFixed(2) + '%';
        console.log('原始得分计算:', scoreFormula);
        console.log('订正得分计算:', fixScoreFormula);
      }
    },
    
    // 获取统计数据
    async fetchStatistics() {
      try {
        const params = {
          game_name: this.filterForm.game_name,
          game_type: this.filterForm.game_type,
          game_level: this.filterForm.game_level,
          fix_status: this.filterForm.fix_status,
          replay_status: this.filterForm.replay_status,
          game_time_start: this.filterForm.game_time?.[0] || '',
          game_time_end: this.filterForm.game_time?.[1] || ''
        }
        
        const res = await getGameStatistics(params)
        if (res.code === 200) {
          // 合并所有返回的统计数据
          this.statistics = {
            ...this.statistics,
            ...res.data
          }
          
          // 确保题目总数字段存在
          if (this.statistics.total_questions === undefined || this.statistics.total_questions === null) {
            this.statistics.total_questions = 0
          }
          
          // 确保平均难度字段存在
          if (this.statistics.avg_difficulty === undefined || this.statistics.avg_difficulty === null) {
            this.statistics.avg_difficulty = null
          }
          
          console.log('统计数据更新:', this.statistics)
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      }
    },
    
    // 显示统计信息弹窗
    showStatisticsDialog() {
      // 切换弹窗显示状态，如果已显示则关闭，如果未显示则打开
      this.statisticsDialogVisible = !this.statisticsDialogVisible;
    },
    
    // 显示查询弹窗
    showFilterDialog() {
      // 切换弹窗显示状态，如果已显示则关闭，如果未显示则打开
      this.filterDialogVisible = !this.filterDialogVisible;
    },
    
    // 判断某题目的操作面板是否展开
    isQuestionActionsOpen(question) {
      const code = question && (question.question_code || question.id)
      return this.openActionQuestionCode === code
    },
    // 切换题目操作面板展开/收起
    toggleQuestionActions(question) {
      const code = question && (question.question_code || question.id)
      if (!code) return
      this.openActionQuestionCode = this.openActionQuestionCode === code ? null : code
    },
    
    // 处理卡片点击
    async handleCardClick(game) {
      console.log('卡片被点击:', game.game_name)
      this.currentGame = game
      
      // 获取题目信息
      try {
        const res = await getQuestions(game.game_code)
        if (res.code === 200) {
          this.gameQuestions = res.data || []
          console.log('获取题目成功:', this.gameQuestions.length)
          
          // 全面调试题目数据结构
          if (this.gameQuestions.length > 0) {
            const firstQuestion = this.gameQuestions[0];
            console.log('题目完整数据:', firstQuestion)
            // 查看所有可能的难度相关字段
            console.log('难度相关字段:', {
              difficulty: firstQuestion.difficulty,
              question_level: firstQuestion.question_level,
              level: firstQuestion.level,
              question_difficulty: firstQuestion.question_difficulty,
              difficultyLevel: firstQuestion.difficultyLevel,
              difficulty_level: firstQuestion.difficulty_level,
              difficultyText: firstQuestion.difficultyText
            })
            
            // 遍历所有字段寻找难度信息
            console.log('所有字段名:')
            for (const key in firstQuestion) {
              console.log(`${key}: ${firstQuestion[key]}`)
            }
          }
          
          // 确保题目对象完整，并转换难度为标准格式
          this.gameQuestions = this.gameQuestions.map(q => {
            // 创建新对象，避免修改原始数据
            const question = {...q};
            
            // 调试单个题目的难度信息
            console.log(`题目难度信息 - ID: ${question.question_code}`, {
              question_level: question.question_level,
              difficulty: question.difficulty,
              difficultyText: question.difficultyText
            });
            
            // 查找和应用难度信息
            let difficultyField = null;
            let isUndefined = false;
            
            // 按优先级尝试不同字段
            if (question.question_level && question.question_level !== '') {
              difficultyField = question.question_level;
            } else if (question.difficulty && question.difficulty !== '') {
              difficultyField = question.difficulty;
            } else if (question.difficultyText && question.difficultyText !== '') {
              difficultyField = question.difficultyText;
            } else if (question.level && question.level !== '') {
              difficultyField = question.level;
            } else {
              // 如果找不到任何难度信息，明确标记为未定义
              isUndefined = true;
            }
            
            // 确保有一个标准的difficulty字段用于显示
            question.difficulty = isUndefined ? '' : this.mapToStandardLevel(difficultyField);

            // 调试难度映射
            console.log(`题目(${question.question_code})难度映射:`, {
              原始值: difficultyField,
              映射结果: question.difficulty,
              未定义标记: isUndefined,
              最终样式: this.getQuestionLevelStyle(question.difficulty)
            });
            
            return question;
          });
          
          // 调试最终处理后的题目
          if (this.gameQuestions.length > 0) {
            console.log('处理后第一题难度:', {
              difficulty: this.gameQuestions[0].difficulty,
              mappedStyle: this.getQuestionLevelStyle(this.gameQuestions[0].difficulty)
            });
          }
        } else {
          this.gameQuestions = []
          console.log('获取题目返回错误码:', res.code)
        }
      } catch (error) {
        console.error('获取题目失败:', error)
        this.gameQuestions = []
      }
      
      console.log('显示详情弹窗')
      this.detailDialogVisible = true
    },
    
    // 处理查询
    handleSearch() {
      // 将开始日期和结束日期转换为game_time数组格式
      if (this.filterForm.start_date || this.filterForm.end_date) {
        this.filterForm.game_time = [
          this.filterForm.start_date || '',
          this.filterForm.end_date || ''
        ];
      } else {
        this.filterForm.game_time = [];
      }
      
      this.currentPage = 1
      this.gameList = []
      this.fetchGameList()
      this.fetchStatistics()
      this.filterDialogVisible = false
    },
    
    // 重置查询条件
    handleReset() {
      this.filterForm = {
        game_name: '',
        game_type: '',
        game_level: '',
        fix_status: '',
        replay_status: '',
        start_date: '',
        end_date: '',
        game_time: []
      }
    },
    
    // 加载更多
    loadMore() {
      if (!this.hasMore || this.loading) return
      
      this.currentPage++
      this.fetchGameList(true)
    },
    
    // 处理滚动事件，实现自动加载更多
    handleScroll(event) {
      const container = event.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      
      // 检测滚动方向并控制头部横幅显示
      const deltaY = scrollTop - this.lastScrollTop
      
      if (Math.abs(deltaY) > this.scrollThreshold) {
        if (deltaY > 0) {
          // 向下滚动（内容向上移动），隐藏头部横幅
          this.headerVisible = false
        } else {
          // 向上滚动（内容向下移动），显示头部横幅
          this.headerVisible = true
        }
      }
      
      this.lastScrollTop = scrollTop
      
      // 当滚动到距离底部100px时，加载更多内容（仅在满足条件时）
      if (!this.loading && this.hasMore && scrollHeight - scrollTop - clientHeight < 100) {
        this.loadMore()
      }
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '未设置'
      return date.split(' ')[0] // 只取日期部分
    },
    
    // 格式化分数
    formatScore(score, fullScore) {
      if (!score && score !== 0) return '未设置'
      if (!fullScore && fullScore !== 0) return score
      return `${score}/${fullScore}`
    },
    
    // 格式化排名
    formatRank(rank, totalRank) {
      if (!rank && rank !== 0) return '未设置'
      if (!totalRank && totalRank !== 0) return rank
      return `${rank}/${totalRank}`
    },
    
    // 格式化难度
    formatDifficulty(difficulty) {
      if (!difficulty && difficulty !== 0) return '未设置'
      const difficultyMap = {
        1: '简单',
        2: '中等',
        3: '困难',
        4: '非常困难',
        5: '极其困难'
      }
      return difficultyMap[difficulty] || '未知'
    },
    
    // 获取分数样式类名
    getScoreClass(scoreRate) {
      if (!scoreRate && scoreRate !== 0) return ''
      if (scoreRate >= 90) return 'score-excellent'
      if (scoreRate >= 75) return 'score-good'
      if (scoreRate >= 60) return 'score-fair'
      return 'score-poor'
    },
    
    // 获取排名样式类名
    getRankClass(rankRate) {
      if (!rankRate && rankRate !== 0) return ''
      // 排名率越低越好
      if (rankRate <= 5) return 'rank-excellent'
      if (rankRate <= 20) return 'rank-good'
      if (rankRate <= 50) return 'rank-fair'
      return 'rank-poor'
    },
    
    // 获取难度样式类名
    getDifficultyClass(difficulty) {
      if (!difficulty && difficulty !== 0) return ''
      if (difficulty === 1) return 'difficulty-easy'
      if (difficulty === 2) return 'difficulty-medium'
      if (difficulty === 3) return 'difficulty-hard'
      if (difficulty === 4) return 'difficulty-very-hard'
      if (difficulty === 5) return 'difficulty-extreme'
      return ''
    },
    
    // 格式化百分比
    formatPercentage(value) {
      if (value === undefined || value === null) return '0.00%'
      return value.toFixed(2) + '%'
    },

    // 格式化数字
    formatNumber(value) {
      if (value === undefined || value === null) return '0'
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },

    // 获取分数颜色 (根据得分率)
    getScoreColorByPercentage(scoreRate) {
      // 如果没有直接提供score_rate，则需要手动计算
      let rate = scoreRate;
      
      if (scoreRate === null || scoreRate === undefined) {
        // 尝试从当前上下文计算得分率
        const context = this._vnode.context;
        if (context && context.game) {
          const game = context.game;
          if (game.total_score !== undefined && game.question_num) {
            rate = (game.total_score / (game.question_num * 100)) * 100;
          } else {
            return '#606266'; // 默认灰色
          }
        } else {
          return '#606266'; // 默认灰色
        }
      }
      
      // 根据得分率返回对应颜色
      if (rate >= 90) return '#237804'; // 绿色
      if (rate >= 75) return '#52C41A'; // 黄绿色
      if (rate >= 50) return '#F7BA2A'; // 黄色
      if (rate >= 25) return '#E6A23C'; // 橙色
      return '#F56C6C'; // 红色
    },

    // 获取分数颜色 - 参照QuestionList.vue
    getScoreColor(score) {
      if (score === undefined || score === null) return '#606266';
      const percentage = (score / 100) * 100;
      if (percentage >= 100) return '#237804'; // 绿色
      if (percentage >= 75) return '#52C41A'; // 黄绿色
      if (percentage >= 50) return '#F7BA2A'; // 黄色
      if (percentage >= 25) return '#E6A23C'; // 橙色
      return '#F56C6C'; // 红色
    },
    
    // 计算订正得分率
    getFixScoreRate(game) {
      if (!game || !game.question_num || game.question_num === 0) return 0;
      const fixScore = game.total_score_fix || game.total_score || 0;
      return (fixScore / (game.question_num * 100)) * 100;
    },
    
    // 保留兼容性方法 - 获取分数比率
    getScoreRate(score, fullScore) {
      if (score === undefined || score === null) return 0;
      if (fullScore === undefined || fullScore === null || fullScore === 0) {
        // 假设满分为100
        fullScore = 100;
      }
      return (score / fullScore) * 100;
    },

    // 获取难度颜色
    getDifficultyColorByPercentage(percentage, opacity = 1) {
      if (percentage === null || percentage === undefined) return `rgba(144, 147, 153, ${opacity})`; // 默认灰色
      if (percentage <= 14.29) return `rgba(245, 108, 108, ${opacity})`; // 红题
      if (percentage <= 28.58) return `rgba(230, 162, 51, ${opacity})`; // 橙题
      if (percentage <= 42.87) return `rgba(247, 186, 42, ${opacity})`; // 黄题
      if (percentage <= 57.15) return `rgba(103, 194, 58, ${opacity})`; // 绿题
      if (percentage <= 71.44) return `rgba(64, 158, 255, ${opacity})`; // 蓝题
      if (percentage <= 85.72) return `rgba(142, 68, 173, ${opacity})`; // 紫题
      return `rgba(44, 62, 80, ${opacity})`; // 黑题
    },

    // 格式化难度百分比
    formatDifficultyPercentage(value) {
      if (value === null || value === undefined) return '0.00%'
      return value.toFixed(2) + '%'
    },

    // 获取题目难度标签文本
    getQuestionLevelLabel(level) {
      // 如果是存在于QUESTION_LEVELS中的值
      const levelConfig = QUESTION_LEVELS.find(item => item.value === level);
      if (levelConfig) {
        return levelConfig.label;
      }
      
      // 尝试数字映射
      if (typeof level === 'number' || !isNaN(Number(level))) {
        const num = Number(level);
        // 数字到颜色的映射（对应难度值）
        switch(num) {
          case 1: return '红题';
          case 2: return '橙题';
          case 3: return '黄题';
          case 4: return '绿题'; 
          case 5: return '蓝题';
          case 6: return '紫题';
          case 7: return '黑题';
        }
      }
      
      // 尝试字符串匹配
      if (typeof level === 'string') {
        if (level.includes('红')) return '红题';
        if (level.includes('橙')) return '橙题';
        if (level.includes('黄')) return '黄题';
        if (level.includes('绿')) return '绿题';
        if (level.includes('蓝')) return '蓝题';
        if (level.includes('紫')) return '紫题';
        if (level.includes('黑')) return '黑题';
      }
      
      // 未能匹配的情况
      return '未定义';
    },

    // 获取题目难度标签样式
    getQuestionLevelStyle(level) {
      // 直接使用原始样式函数
      return getOriginalQuestionLevelStyle(this.mapToStandardLevel(level));
    },
    
    // 将各种可能的难度值映射为标准难度
    mapToStandardLevel(level) {
      // 处理null、undefined或空字符串
      if (level === null || level === undefined || level === '') {
        return ''; // 对应QUESTION_LEVELS中的未定义（value为空字符串）
      }
      
      // 如果已经是标准值
      if (QUESTION_LEVELS.some(item => item.value === level)) {
        return level;
      }
      
      // 数字映射
      if (typeof level === 'number' || !isNaN(Number(level))) {
        const num = Number(level);
        switch(num) {
          case 1: return '红题';
          case 2: return '橙题';
          case 3: return '黄题';
          case 4: return '绿题';
          case 5: return '蓝题';
          case 6: return '紫题';
          case 7: return '黑题';
        }
      }
      
      // 字符串匹配
      if (typeof level === 'string') {
        if (level.includes('红')) return '红题';
        if (level.includes('橙')) return '橙题';
        if (level.includes('黄')) return '黄题';
        if (level.includes('绿')) return '绿题';
        if (level.includes('蓝')) return '蓝题';
        if (level.includes('紫')) return '紫题';
        if (level.includes('黑')) return '黑题';
        if (level.toLowerCase().includes('undefined') || level.includes('未定义')) return '';
      }
      
      // 默认返回未定义
      return '';
    },

    // 获取标签颜色类名 - 使用QuestionList.vue中的逻辑
    getTagColorClass(tagCode) {
      // 将tag_code转换为数字并取模，确保有颜色
      if (!tagCode) return 'tag-color-1';
      
      const colors = [
        'tag-color-1', 'tag-color-2', 'tag-color-3', 'tag-color-4', 'tag-color-5',
        'tag-color-6', 'tag-color-7', 'tag-color-8', 'tag-color-9', 'tag-color-10'
      ];
      
      // 根据tagCode的最后一位数字选择颜色
      const lastChar = tagCode.slice(-1);
      const index = parseInt(lastChar, 10) % colors.length;
      return colors[index] || colors[0];
    },

    // 获取难度文本颜色
    getDifficultyTextColor(value) {
      if (value === null || value === undefined) {
        return '#909399' // 默认灰色
      }
      if (value < 14.29) return '#6D5B44'
      if (value < 28.58) return '#754B3A'
      if (value < 42.87) return '#3B5135'
      if (value < 57.15) return '#4B3853'
      if (value < 71.44) return '#2A3F58'
      if (value < 85.72) return '#6B5427'
      return '#5A4B1D'
    },

    // 获取难度块颜色
    getDifficultyBlockColor(value) {
      if (value === null || value === undefined) {
        return '#909399' // 默认灰色
      }
      if (value < 14.29) return '#F56C6C' // 红题
      if (value < 28.58) return '#E6A23C' // 橙题
      if (value < 42.87) return '#F7BA2A' // 黄题
      if (value < 57.15) return '#67C23A' // 绿题
      if (value < 71.44) return '#409EFF' // 蓝题
      if (value < 85.72) return '#8E44AD' // 紫题
      return '#2C3E50' // 黑题
    },
    
    // 显示复盘弹窗
    showReplay(game) {
      console.log('显示复盘对话框，游戏信息:', game);
      // 确保设置当前游戏信息
      this.currentGame = game;
      // 启用复盘对话框
      this.replayDialogVisible = true;
    },
    
         // 处理题目订正
     async handleQuestionFix(question) {
       try {
         await this.mobileConfirm('确认将该题目订正分数改为100分吗？')
         
         const res = await request({
           url: `/api/game/question/${question.question_code}/fix`,
           method: 'put'
         })
         
         if (res.code === 200) {
           this.$message.success('订正成功')
           // 更新本地状态
           question.question_score_fix = 100
           // 刷新题目列表
           await this.handleCardClick(this.currentGame)
           // 收起操作面板
           this.openActionQuestionCode = null
         } else {
           throw new Error(res.message || '订正失败')
         }
       } catch (error) {
         if (error !== 'cancel') {
           console.error('订正失败:', error)
           this.$message.error(error.message || '订正失败')
         }
       }
     },
     
     // 处理题目收藏
     async handleQuestionCollect(question) {
       try {
         await this.mobileConfirm(
           question.is_collect ? '确认取消收藏该题目吗？' : '确认收藏该题目吗？'
         )

         const res = await updateQuestionCollect(question.question_code, !question.is_collect)
         if (res.code === 200) {
           // 更新本地状态
           question.is_collect = !question.is_collect
           this.$message.success(question.is_collect ? '收藏成功' : '取消收藏成功')
           // 收起操作面板
           this.openActionQuestionCode = null
         } else {
           this.$message.error(res.message || '操作失败')
         }
       } catch (error) {
         if (error !== 'cancel') {
           console.error('收藏操作失败:', error)
           this.$message.error('操作失败')
         }
       }
     },
     
     // 处理题目挂起
     async handleQuestionPending(question) {
       try {
         // 判断题目得分和订正分数
         if (question.question_score === 100 || question.question_score_fix === 100) {
           this.$message.warning('该题目你已经' + (question.question_score === 100 ? 'AC' : '订正') + '，无法挂起。')
           return
         }

         await this.mobileConfirm(
           question.is_pending ? '确认取消挂起该题目吗？' : '确认挂起该题目吗？'
         )
         
         const res = await request({
           url: `/api/question/${question.question_code}/pending`,
           method: 'put',
           data: {
             is_pending: !question.is_pending
           }
         })
         
         if (res.code === 200) {
           // 更新本地状态
           question.is_pending = !question.is_pending
           this.$message.success(question.is_pending ? '挂起成功' : '取消挂起成功')
           // 收起操作面板
           this.openActionQuestionCode = null
         } else {
           this.$message.error(res.message || '操作失败')
         }
       } catch (error) {
         if (error !== 'cancel') {
           console.error('挂起操作失败:', error)
           this.$message.error('操作失败')
         }
       }
     },
     
     // 移动端确认对话框
     mobileConfirm(message) {
       return new Promise((resolve, reject) => {
         this.$confirm(message, '提示', {
           confirmButtonText: '确定',
           cancelButtonText: '取消',
           type: 'warning',
           customClass: 'mobile-confirm-dialog',
           center: true,
           closeOnClickModal: true,
         }).then(() => resolve()).catch((err) => reject(err || 'cancel'))
       })
     },
     
     // 获取赛事级别标签样式
     getGameLevelStyle(level) {
       const levelStyles = {
         'S-提高级': { backgroundColor: '#8E44AD', color: '#ffffff' }, // 紫色 - S级
         'J-入门级': { backgroundColor: '#67C23A', color: '#ffffff' }, // 绿色 - J级
         'N-NOIP': { backgroundColor: '#E6A23C', color: '#ffffff' }, // 橙色 - NOIP
         'NOI': { backgroundColor: '#F56C6C', color: '#ffffff' }, // 红色 - NOI
         'Div.1': { backgroundColor: '#409EFF', color: '#ffffff' }, // 蓝色 - Div.1
         'Div.2': { backgroundColor: '#5DADE2', color: '#ffffff' }, // 浅蓝色 - Div.2
         'Div.3': { backgroundColor: '#85C1E9', color: '#ffffff' }, // 更浅蓝色 - Div.3
         'Div.4': { backgroundColor: '#AED6F1', color: '#ffffff' }, // 最浅蓝色 - Div.4
         'ABC': { backgroundColor: '#48C9B0', color: '#ffffff' }, // 青色 - ABC
         'ARC': { backgroundColor: '#AF7AC5', color: '#ffffff' }, // 淡紫色 - ARC
         'AGC': { backgroundColor: '#EC7063', color: '#ffffff' }, // 淡红色 - AGC
         '自定义等级': { backgroundColor: '#909399', color: '#ffffff' } // 灰色 - 自定义
       }
       
       return levelStyles[level] || { backgroundColor: '#909399', color: '#ffffff' }
     },
     getErrorOutlineStyle
  }
}
</script>

<style lang="scss" scoped>
.game-mobile-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
  position: relative;
  overflow: hidden;
}

/* 移动端操作按钮 - 与App.vue中全局菜单按钮样式保持一致 */
.mobile-action-buttons {
  position: fixed;
  right: 15px;
  bottom: 135px; /* 距离底部的距离 */
  display: flex;
  flex-direction: column;
  gap: 15px; /* 按钮之间的垂直间距 */
  z-index: 2001;
  width: 40px; /* 限制容器宽度与按钮一致 */
  align-items: center; /* 确保按钮水平居中对齐 */
  padding: 0 !important;
  margin: 0 !important;
  
  /* 确保每个按钮容器没有额外的边距和内边距 */
  .el-button {
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }
}

.mobile-icon-btn {
  width: 40px !important; /* 与App.vue保持一致 */
  height: 40px !important; /* 与App.vue保持一致 */
  border-radius: 40px !important; /* 与App.vue保持一致 */
  background-color: rgba(255, 255, 255, 0.5) !important; /* 与App.vue保持一致 */
  color: #409eff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important; /* 确保没有外边距干扰对齐 */
  transition: all 0.3s ease !important;
  position: relative !important; /* 使用相对定位 */
  left: 0 !important; /* 确保没有左偏移 */
  
  i {
    font-size: 20px !important; /* 与App.vue保持一致 */
    margin: 0 !important; /* 确保图标没有外边距 */
    padding: 0 !important; /* 确保图标没有内边距 */
    display: inline-flex !important; /* 使用inline-flex布局 */
    justify-content: center !important; /* 水平居中 */
    align-items: center !important; /* 垂直居中 */
    width: 20px !important; /* 固定宽度 */
    height: 20px !important; /* 固定高度 */
  }
  
  &:hover, &:focus {
    background-color: rgba(255, 255, 255, 0.8) !important; /* 与App.vue保持一致 */
    transform: scale(1.05) !important;
  }
  
  &:active {
    background-color: rgba(255, 255, 255, 0.9) !important; /* 与App.vue保持一致 */
    transform: scale(0.95) !important;
  }
}

/* 标题栏 */
.mobile-header {
  height: 50px;
  background-color: #304156; /* 与App.vue中的.mobile-drawer背景色保持一致 */
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent); /* 添加与抽屉菜单相同的渐变效果 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08); /* 添加与抽屉菜单相同的底部边框 */
  transition: all 0.3s ease;
  
  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }
  
  /* 隐藏状态 */
  &.header-hidden {
    transform: translateY(-100%);
    opacity: 0;
  }
}

/* 卡片容器 */
.game-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 20px;
  padding-top: 60px; /* 为固定定位的头部预留空间 */
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
  transition: padding-top 0.3s ease; /* 添加顶部内边距的过渡动画 */
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* 当头部隐藏时，减少顶部内边距 */
  &.header-hidden {
    padding-top: 10px;
  }
  
  /* 赛事卡片 */
  .game-card {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    margin-bottom: 14px;
    overflow: hidden;
    transition: all 0.3s ease;
    width: 100%;
    display: flex; /* 使用flex布局 */
    align-items: flex-start; /* 对齐卡片顶部 */
    position: relative; /* 相对定位 */
    border: 1px solid #ebeef5;
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }
    
    &:last-child {
      margin-bottom: 20px; /* 最后一张卡片底部增加间距 */
    }
    
    /* 难度指示器 - 右上角三角形 */
    .difficulty-indicator {
      position: absolute;
      top: 0;
      right: 0;
      width: 60px;
      height: 60px;
      overflow: hidden;
      z-index: 1;
      pointer-events: none; /* 确保点击事件透过指示器 */
      
      .triangle-background {
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 60px 60px 0;
        opacity: 0.85; /* 稍微降低不透明度，使其更和谐 */
      }
      
      .difficulty-value {
        position: absolute;
        /* 调整位置确保完全居中显示 */
        top: 12px;
        right: 12px;
        /* 使文本平行于斜边 */
        transform: rotate(45deg);
        color: white;
        font-size: 12px;
        font-weight: bold;
        text-align: center;
        white-space: nowrap;
        width: 30px; /* 调整宽度确保文本居中 */
        line-height: 1;
        text-shadow: 0 1px 1px rgba(0,0,0,0.3);
        letter-spacing: 0px; /* 略微调整字间距使百分比更紧凑 */
      }
    }

    /* 订正水印/印章 */
    .fix-stamp {
      position: absolute;
      top: 45px; /* 定位在得分位置上方 */
      right: 10%;
      transform: translateY(-50%) rotate(-15deg);
      pointer-events: none; /* 确保印章不影响点击 */
      z-index: 2; /* 确保印章在卡片内容之上 */
      
      .stamp-content {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background-color: rgba(103, 194, 58, 0.6); // 绿色 - 订正，增加透明度
        color: white;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 24px;
        font-weight: bold;
        border: 2px solid rgba(255, 255, 255, 0.6);
        box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        
        /* AK印章的样式 */
        &.ak-stamp {
          background-color: rgba(247, 186, 42, 0.6); // 黄色 - AK，增加透明度
        }
      }
    }

    .game-card-main {
      padding: 15px;
      padding-top: 10px;
      flex: 1; /* 占据剩余空间 */
      width: 100%; /* 确保内容区域占满宽度 */
      
      .game-header {
        margin-bottom: 10px;
        padding-right: 50px; /* 为右上角三角形留出更多空间 */
        
        .game-name {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
          line-height: 22px; /* 固定行高22px */
          word-break: break-word;
          margin: 0;
          padding: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2; /* 最多显示2行 */
          line-clamp: 2; /* 标准属性 */
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          min-height: 22.5px; /* 1行固定高度：22px */
          max-height: 45px; /* 2行固定高度：22px * 2 = 44px */
          
          // 录像机图标（内联显示）
          .replay-icon {
            font-size: 15px; /* 图标稍小一点 */
            color: #409EFF;
            background-color: rgba(64, 158, 255, 0.15);
            border-radius: 50%;
            padding: 2px; /* 减小padding */
            margin-right: 6px;
            vertical-align: 0px; /* 使用微调，确保视觉居中 */
            cursor: pointer;
            transition: all 0.3s;
            line-height: 1;
            display: inline-block;
            height: 18px; /* 固定高度：14px + 2px*2 = 18px */
            width: 18px; /* 固定宽度保持圆形 */
            box-sizing: border-box;
            
            &:hover {
              background-color: rgba(64, 158, 255, 0.25);
            }
          }
          
          // 赛事级别标签（内联显示）
          .game-level-tag {
            font-size: 11px;
            padding: 2px 6px; /* 减小上下padding */
            border-radius: 4px;
            margin-right: 6px;
            font-weight: 600;
            white-space: nowrap;
            vertical-align: 1px; /* 使用微调，确保视觉居中 */
            display: inline-block;
            line-height: 1.3; /* 调整行高 */
            height: 17px; /* 固定高度：11px * 1.3 ≈ 14.3px + 2px*2 padding = 18.3px，取17px */
            box-sizing: border-box;
          }
        }
      }
      
      .game-info {
        margin-top: 3px;
        border-top: 1px solid #f5f5f5;
        padding-top: 8px;
        
        .game-type-date {
          font-size: 12px;
          color: #909399;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          
          .flag-icon {
            margin-right: 5px;
            margin-left: 2px;
            font-size: 12px;
          }
        }
        
        .game-data-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: 8px;
        }
        
        .game-score,
        .game-rank {
          font-size: 12px;
          display: flex;
          align-items: center;
          background-color: rgba(249, 250, 252, 0.8);
          border-radius: 6px;
          padding: 6px 10px;
          border: 1px solid #f0f2f5;
          
          .score-label,
          .rank-label {
            color: #606266;
            margin-right: 5px;
            font-weight: 500;
            min-width: 40px;
            text-align: right;
          }
          
          .score-value-container,
          .rank-value-container {
            display: flex;
            align-items: center;
            gap: 3px;
            flex: 1;
            justify-content: flex-start;
          }
          
          .score-value,
          .rank-value {
            font-weight: 600;
          }
          
          .score-separator,
          .rank-separator {
            color: #c0c4cc;
            font-weight: 400;
            padding: 0 1px;
          }
        }
      }
    }
  }
  
  /* 暂无数据样式 */
  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    
    .no-data-content {
      text-align: center;
      padding: 30px;
      
      i {
        font-size: 50px;
        color: #dcdfe6;
        margin-bottom: 20px;
      }
      
      .no-data-title {
        font-size: 18px;
        color: #606266;
        font-weight: 500;
        margin-bottom: 10px;
        display: block;
      }
      
      .no-data-tips {
        font-size: 14px;
        color: #909399;
      }
    }
  }
  
  /* 加载更多区域 */
  .load-more, .loading-more, .no-more {
    text-align: center;
    margin-top: 10px;
    padding: 10px 0;
    
    .el-button {
      font-size: 14px;
    }
  }
  
  .loading-more {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
    color: #909399;
    
    i {
      font-size: 16px;
    }
  }
  
  .no-more {
    text-align: center;
    padding-bottom: 20px;
    
    span {
      color: #909399;
      font-size: 12px;
    }
    
    .copyright-info {
      text-align: center;
      width: 100%;
      margin: 15px auto 0;
      display: block;
      padding: 0 15px 15px;
      box-sizing: border-box;
      
      .mobile-copyright {
        text-align: center;
        width: 100%;
        color: #909399;
        font-size: 12px;
        line-height: 1.5;
      }
    }
  }
}

/* 移动端统计网格 */
.mobile-statistics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 2px 0;
  max-height: none;
  overflow-y: visible;
  
  .stat-item {
    background-color: #f9fafc;
    border-radius: 8px;
    padding: 8px 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    min-height: 65px;
    height: auto;
    
    &:active {
      transform: scale(0.98);
    }
    
    /* 给不同卡片添加渐变背景色 */
    &:nth-child(1) {
      background: linear-gradient(to top, 
        rgba(226, 206, 186, 0.3) 0%, 
        rgba(226, 206, 186, 1) 100%);
      .stat-label, i { color: #8D7B64; }
      .stat-value { color: #6D5B44; }
    }
    &:nth-child(2) {
      background: linear-gradient(to top, 
        rgba(209, 178, 164, 0.3) 0%, 
        rgba(209, 178, 164, 1) 100%);
      .stat-label, i { color: #956B5A; }
      .stat-value { color: #754B3A; }
    }
    &:nth-child(3) {
      background: linear-gradient(to top, 
        rgba(190, 206, 185, 0.3) 0%, 
        rgba(190, 206, 185, 1) 100%);
      .stat-label, i { color: #5B7155; }
      .stat-value { color: #3B5135; }
    }
    &:nth-child(4) {
      background: linear-gradient(to top, 
        rgba(199, 188, 205, 0.3) 0%, 
        rgba(199, 188, 205, 1) 100%);
      .stat-label, i { color: #6B5873; }
    }
    &:nth-child(5) {
      background: linear-gradient(to top, 
        rgba(187, 199, 214, 0.3) 0%, 
        rgba(187, 199, 214, 1) 100%);
      .stat-label, i { color: #4A5F78; }
      .stat-value { color: #2A3F58; }
    }
    &:nth-child(6) {
      background: linear-gradient(to top, 
        rgba(220, 198, 170, 0.3) 0%, 
        rgba(220, 198, 170, 1) 100%);
      .stat-label, i { color: #8B7447; }
      .stat-value { color: #6B5427; }
    }
    &:nth-child(7) {
      background: linear-gradient(to top, 
        rgba(215, 185, 185, 0.3) 0%, 
        rgba(215, 185, 185, 1) 100%);
      .stat-label, i { color: #8E5D5D; }
      .stat-value { color: #6E3D3D; }
    }
    &:nth-child(8) {
      background: linear-gradient(to top, 
        rgba(176, 200, 197, 0.3) 0%, 
        rgba(176, 200, 197, 1) 100%);
      .stat-label, i { color: #4A6764; }
      .stat-value { color: #2A4744; }
    }
    &:nth-child(9) {
      background: linear-gradient(to top, 
        rgba(195, 182, 154, 0.3) 0%, 
        rgba(195, 182, 154, 1) 100%);
      .stat-label, i { color: #7A6B3D; }
      .stat-value { color: #5A4B1D; }
    }
    &:nth-child(10) {
      background: linear-gradient(to top, 
        rgba(180, 195, 203, 0.3) 0%, 
        rgba(180, 195, 203, 1) 100%);
      .stat-label, i { color: #546E7A; }
      .stat-value { color: #37474F; }
    }
    
    .stat-value {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 5px;
      text-align: center;
      line-height: 1.2;
    }
    
    .stat-label {
      font-size: 11px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      i {
        margin-right: 3px;
        font-size: 13px;
      }
    }
  }
  
  /* 难度颜色块样式 */
  .difficulty-color-block {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 3px;
    vertical-align: -1px;
    flex-shrink: 0;
  }
  
  /* 难度值样式 */
  .difficulty-value {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    
    span {
      display: flex;
      align-items: center;
    }
  }
}

/* 调整弹窗样式以适应统计卡片 */
.statistics-dialog-body {
  max-height: none;
  overflow-y: visible;
  padding: 12px 10px;
}

/* 自定义统计对话框标题 */
.dialog-custom-title {
  font-size: 16px;
  text-align: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

/* 弹窗中的详情样式 */
.game-detail {
  .detail-header {
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 15px;
    margin-bottom: 15px;
    position: relative; /* 为可能添加的难度指示器做准备 */
    
    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0;
      word-break: break-word;
      
      /* 赛事名称链接样式 */
      .game-name-link {
        color: #409EFF;
        text-decoration: none;
        transition: all 0.3s ease;
        
        &:hover {
          color: #66b1ff;
          text-decoration: underline;
        }
      }
    }
    
    /* 详情页的订正水印/印章样式 */
    .detail-fix-stamp {
      position: absolute;
      top: 20px;
      right: 10px;
      pointer-events: none; /* 确保印章不影响点击 */
      z-index: 2; /* 确保印章在卡片内容之上 */
      
      .stamp-content {
        color: rgba(134, 195, 166, 0.9); /* 绿色字体 */
        font-size: 55px;
        font-weight: bold;
        transform: rotate(-30deg); /* 旋转角度 */
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 80px;
        height: 80px;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        
        /* 创建双层弧形边框效果 */
        &:before, &:after {
          content: '';
          position: absolute;
          border-radius: 50%; /* 圆形边框 */
        }
        
        &:before {
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          border: 2px solid rgba(134, 195, 166, 0.9);
          opacity: 0.8;
        }
        
        &:after {
          top: -6px;
          left: -6px;
          right: -6px;
          bottom: -6px;
          border: 1px dashed rgba(134, 195, 166, 0.9);
          opacity: 0.7;
        }
        
        /* AK印章的样式 */
        &.ak-stamp {
          color: rgba(239, 198, 17, 0.95); /* 金黄色字体 */
          
          &:before {
            border: 2px solid rgba(239, 198, 17, 0.95);
            opacity: 0.9;
          }
          
          &:after {
            border: 1px dashed rgba(239, 198, 17, 0.95);
            opacity: 0.8;
          }
        }
      }
    }
  }
  
  .detail-content {
    .detail-item {
      display: flex;
      margin-bottom: 15px;
      
      .detail-label {
        min-width: 80px;
        color: #909399;
        font-size: 14px;
      }
      
      .detail-value {
        flex: 1;
        font-size: 14px;
        color: #606266;
        font-weight: 500;
        word-break: break-word;
        
        &.score-value-container,
        &.rank-value-container {
          display: flex;
          align-items: center;
          gap: 4px;
          
          span {
            font-weight: 600;
          }
          
          .score-separator,
          .rank-separator {
            color: #606266;
            font-weight: normal;
          }
        }
        
        /* 难度标签新样式 */
        .difficulty-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 65px;
          padding: 3px 10px;
          border-radius: 4px;
          color: white;
          font-size: 12px;
          font-weight: 600;
          text-align: center;
          
          &.no-difficulty {
            background-color: #909399;
          }
        }
      }
    }
    
    .detail-section {
      margin-top: 20px;
      
      h4 {
        font-size: 16px;
        color: #303133;
        margin: 0 0 15px 0;
        font-weight: 600;
        border-left: 3px solid #409EFF;
        padding-left: 10px;
      }
      
      .question-list {
        .question-item {
          background-color: #f9fafc !important;
          border-radius: 8px;
          padding: 10px;
          margin-bottom: 10px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
          border: 1px solid #ebeef5;
          
          .question-header {
            display: flex;
            align-items: center;
            margin-bottom: 4px;
            
            .question-seq {
              background-color: #409EFF;
              color: white;
              font-size: 12px;
              padding: 2px 8px;
              border-radius: 4px;
              margin-right: 4px;
              flex-shrink: 0;
            }

            .question-level-label {
              flex-shrink: 0;
            }
            
            .question-name {
              font-size: 14px;
              font-weight: 500;
              color: #303133;
              flex: 1;
              line-height: 1.4;
              margin-left: 5px;
              
              /* 题目名称链接样式 */
              .question-name-link {
                color: #409EFF;
                text-decoration: none;
                transition: all 0.3s ease;
                
                &:hover {
                  color: #66b1ff;
                  text-decoration: underline;
                }
              }
            }
          }
          
          .question-info {
            display: flex;
            gap: 15px;
            margin-bottom: 8px;
            flex-wrap: wrap;
            border-top: 1px dashed #ebeef5;
            padding-top: 8px;
            
            .score-cell {
              display: flex;
              align-items: center;
              font-size: 13px;
              margin-right: 12px;
              
              .score-label {
                color: #606266;
                font-weight: normal;
                margin-right: 4px;
              }
              
              i {
                font-size: 16px;
              }
              
              .score-value {
                font-weight: 600;
              }
            }
          }
          
          .question-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 5px;
            margin-top: 8px;
            border-top: 1px dashed #ebeef5;
            padding-top: 8px;
            
            .question-tag {
              margin: 0;
              font-size: 11px;
              padding: 0 8px;
              height: 22px;
              line-height: 20px;
              color: white !important;
              border: none !important;
            }
          }
          
          /* 题目操作按钮（滑出面板内的按钮通用样式） */
          .question-action-btn {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
            padding: 0 !important;
            border-radius: 6px !important;
            background-color: #F2F3F5 !important;
            border: none !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin: 0 !important;
            transition: all 0.3s ease !important;
          }
          
          .question-action-btn i {
            font-size: 16px !important;
          }
          
          .question-action-btn:hover,
          .question-action-btn:focus,
          .question-action-btn:active {
            background-color: #F2F3F5 !important; /* 保持与默认一致，不出现高亮 */
            box-shadow: none !important;
          }

          /* 禁用悬停/聚焦时图标颜色变化，始终由内联style控制 */
          .question-action-btn:hover i,
          .question-action-btn:focus i,
          .question-action-btn:active i {
            color: inherit !important; /* 不随状态改变图标颜色 */
          }

          /* 明确齿轮按钮的图标默认颜色与状态不变 */
          .action-toggle-btn i {
            color: #409EFF !important; /* 品牌蓝，与其他操作图标保持一致 */
          }
          .action-toggle-btn:hover i,
          .action-toggle-btn:focus i,
          .action-toggle-btn:active i {
            color: #409EFF !important;
          }
          
          .question-action-btn:disabled {
            background-color: #F2F3F5 !important;
            opacity: 1 !important;
          }
          
          .question-action-btn:disabled i {
            color: #C0C4CC !important;
          }

          /* 标题行右侧的"操作"按钮与滑出面板 */
          .question-actions-inline {
            margin-left: 8px;
            position: relative;
            display: flex;
            align-items: center;
          }

          .action-toggle-btn {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
            padding: 0 !important;
          }

          .actions-slide-panel {
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateX(110%) translateY(-50%);
            display: flex;
            gap: 8px;
            transition: transform 0.25s ease;
            z-index: 3;
          }

          .actions-slide-panel.open {
            transform: translateX(0) translateY(-50%);
          }
        }
      }
      
      .no-questions {
        text-align: center;
        padding: 20px 0;
        color: #909399;
        
        i {
          font-size: 24px;
          margin-bottom: 10px;
          display: block;
        }
      }
    }
  }
}

/* 所有对话框共有样式 */
::v-deep .statistics-dialog,
::v-deep .filter-dialog,
::v-deep .detail-dialog {
  margin-top: 10vh !important;
  background-color: #ffffff !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3) !important;
  overflow: hidden !important;
  
  /* 移除对话框头部 */
  .el-dialog__header {
    display: none !important;
  }
  
  /* 确保内容区域上下padding一致 */
  .el-dialog__body {
    padding: 20px !important; /* 确保上下左右间距一致 */
    max-height: 75vh !important;
    overflow-y: hidden !important; /* 不显示滚动条 */
    background-color: #ffffff !important;
  }
  
  .el-dialog__headerbtn {
    z-index: 10 !important;
    top: 10px !important;
    right: 10px !important;
  }
  
  /* 确保弹窗位于视窗顶部10%位置 */
  &.detail-dialog {
    margin-top: 10vh !important; /* 距顶部10%位置 */
    animation: none !important; /* 移除可能的动画效果，防止位置跳动 */
    transform-origin: center top !important; /* 从顶部中心缩放 */
  }
}

/* 确保对话框没有额外的边框和内边距 */
::v-deep .el-dialog {
  background-color: #ffffff !important;
  padding: 0 !important;
  border: none !important;
}

/* 强制设置详情弹窗位置 - 使用全局最高优先级选择器 */
body .el-dialog__wrapper .el-dialog.detail-dialog,
#app .el-dialog__wrapper .el-dialog.detail-dialog,
.el-dialog__wrapper .el-dialog.detail-dialog,
html body .el-dialog__wrapper .detail-dialog.custom-no-header,
body > .el-dialog__wrapper > .el-dialog.detail-dialog,
.game-mobile-container + .el-dialog__wrapper .el-dialog.detail-dialog {
  position: fixed !important;
  top: 10vh !important; 
  left: 50% !important;
  transform: translateX(-50%) !important;
  margin: 0 auto !important;
  margin-top: 10vh !important;
}

/* 特别处理没有header的对话框 */
::v-deep .custom-no-header {
  .el-dialog__header {
    display: none !important;
    height: 0 !important;
    max-height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    overflow: hidden !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
    position: absolute !important;
    left: -9999px !important; /* 完全移出视图 */
    width: 0 !important; /* 确保不占用宽度 */
    line-height: 0 !important; /* 确保不占用高度 */
  }
  
  .el-dialog__body {
    padding: 20px !important; /* 保持与其他设置一致的padding */
  }
}

/* 赛事详情滚动容器样式 */
.detail-scroll-container {
  max-height: calc(75vh - 40px); /* 减去padding的高度(20px顶部+20px底部) */
  overflow-y: auto;
  overflow-x: hidden; /* 禁用横向滚动，避免印章造成的轻微位移 */
  touch-action: pan-y; /* 限制触摸仅在纵向滚动 */
  overscroll-behavior-x: contain; /* 防止横向回弹 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Opera */
  }
}

/* 遮罩层样式 */
::v-deep .v-modal {
  opacity: 0.7 !important;
  background-color: #000000 !important;
}

/* 对话框内容样式 */
.dialog-content {
  padding: 5px;
  background-color: #ffffff !important; /* 确保内容区域是白色 */
  
  &.no-title {
    padding-top: 0; /* 移除标题时减少顶部padding */
  }
  
  .dialog-custom-title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 15px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
    text-align: center;
  }
  
  .dialog-footer {
    text-align: right;
    margin-top: 15px;
    padding-top: 10px;
    border-top: 1px solid #ebeef5;
  }
}

/* 自定义弹窗样式 */
.dialog-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  display: flex;
  justify-content: center;
}

.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 2001;
}

.custom-dialog {
  position: absolute;
  z-index: 2002;
  width: 95%;
  max-height: 80vh;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

/* 统计弹窗：与PlanMobile一致的尺寸与padding */
.custom-dialog.stats-dialog {
  height: auto;
  max-height: none;
  width: 92%;
  max-width: 520px;
}
.custom-dialog.stats-dialog .dialog-body {
  max-height: none;
  overflow-y: visible;
  padding: 12px 10px;
}

.dialog-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
}

.dialog-body {
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden; /* 禁用横向滚动，避免印章造成的轻微位移 */
  overscroll-behavior-x: contain; /* 防止横向回弹 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  &.filter-dialog-body {
    padding: 15px 15px 10px;
  }
}

/* 查询弹窗专用样式 */
.filter-dialog-content {
  padding: 0;
  
  .filter-title {
    font-size: 16px;
    margin: 0 0 10px 0;
    padding-bottom: 8px;
  }
}

.filter-form {
  .el-form-item {
    margin-bottom: 10px;
    
    .el-form-item__label {
      padding-right: 5px;
      line-height: 32px;
    }
    
    .el-form-item__content {
      line-height: 32px;
    }
  }
  
  ::v-deep .el-input__inner,
  ::v-deep .el-select .el-input__inner,
  ::v-deep .el-date-editor--daterange.el-input__inner {
    height: 32px;
    line-height: 32px;
  }
  
  ::v-deep .el-range-separator {
    line-height: 24px;
  }
  
  ::v-deep .el-range-input {
    height: 30px;
  }
}

.filter-footer {
  margin-top: 10px;
  padding-top: 8px;
  
  .el-button {
    padding: 7px 15px;
  }
}

/* 滚动容器样式 */
.detail-scroll-container {
  max-height: calc(80vh - 40px);
  overflow-y: auto;
  overflow-x: hidden; /* 禁用横向滚动 */
  touch-action: pan-y; /* 限制触摸仅在纵向滚动 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
}

/* 标签颜色类 - 参照QuestionList.vue */
.tag-color-1, .el-tag.tag-color-1 { background-color: #409EFF !important; border-color: #409EFF !important; color: white !important; }
.tag-color-2, .el-tag.tag-color-2 { background-color: #67C23A !important; border-color: #67C23A !important; color: white !important; }
.tag-color-3, .el-tag.tag-color-3 { background-color: #E6A23C !important; border-color: #E6A23C !important; color: white !important; }
.tag-color-4, .el-tag.tag-color-4 { background-color: #F56C6C !important; border-color: #F56C6C !important; color: white !important; }
.tag-color-5, .el-tag.tag-color-5 { background-color: #8E44AD !important; border-color: #8E44AD !important; color: white !important; }
.tag-color-6, .el-tag.tag-color-6 { background-color: #16A085 !important; border-color: #16A085 !important; color: white !important; }
.tag-color-7, .el-tag.tag-color-7 { background-color: #D35400 !important; border-color: #D35400 !important; color: white !important; }
.tag-color-8, .el-tag.tag-color-8 { background-color: #2980B9 !important; border-color: #2980B9 !important; color: white !important; }
.tag-color-9, .el-tag.tag-color-9 { background-color: #27AE60 !important; border-color: #27AE60 !important; color: white !important; }
.tag-color-10, .el-tag.tag-color-10 { background-color: #C0392B !important; border-color: #C0392B !important; color: white !important; }

/* 日期选择器优化样式 */
.date-range-container {
  display: flex;
  align-items: center;
  gap: 2px;  /* 减少间距 */
  width: 100%;

  .date-picker-item {
    flex: 1;
    
    ::v-deep .el-input__inner {
      font-size: 12px !important;
      padding-left: 25px !important;
      padding-right: 5px !important;
    }
    
    ::v-deep .el-input__prefix {
      left: 3px !important;
    }
    
    ::v-deep .el-input__icon {
      width: 20px !important;
    }
  }
  
  .date-separator {
    flex-shrink: 0;
    color: #909399;
    padding: 0 1px;
    font-size: 12px;
  }
}

/* 修改时间选择器弹出层样式以适应移动端 */
::v-deep .el-picker-panel {
  width: 280px !important; /* 控制日期选择弹窗宽度 */
  
  .el-date-picker__header {
    margin: 8px 12px !important;
  }
  
  .el-picker-panel__content {
    width: 280px !important;
    margin: 0 auto !important;
  }
}

/* 强制日期选择器弹出面板居中显示 */
::v-deep .el-picker-panel[x-placement^="bottom"],
::v-deep .el-picker-panel[x-placement^="top"],
::v-deep .el-date-picker,
::v-deep .el-popper,
::v-deep body > .el-picker-panel {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  right: auto !important;
  bottom: auto !important;
}

/* 禁用Popper.js的定位计算 */
::v-deep .popper__arrow {
  display: none !important;
}

/* 手动处理日期弹窗尺寸 */
::v-deep .el-date-picker {
  width: 280px !important;
  max-width: 90vw !important;
  z-index: 2100 !important; /* 确保在所有元素之上 */
}

/* 确保遮罩层覆盖整个屏幕 */
::v-deep .el-loading-mask {
  z-index: 2050 !important;
}

/* 优化日期表格单元格间距 */
.el-picker-panel .el-date-table th,
.el-picker-panel .el-date-table td {
  padding: 1px !important;
  width: 28px !important;
  height: 28px !important;
}

/* 调整日期单元格内部尺寸 */
.el-picker-panel .el-date-table td .cell {
  width: 26px !important;
  height: 26px !important;
  line-height: 26px !important;
  font-size: 12px !important;
}

/* 优化前进后退按钮 */
.el-picker-panel .el-date-picker__header .el-icon-arrow-left,
.el-picker-panel .el-date-picker__header .el-icon-arrow-right,
.el-picker-panel .el-date-picker__header .el-icon-d-arrow-left,
.el-picker-panel .el-date-picker__header .el-icon-d-arrow-right {
  font-size: 14px !important;
  padding: 0 3px !important;
}

/* 适当减小日期选择器整体宽度，但保证内容显示完整 */
.el-picker-panel .el-date-picker__content {
  width: 100% !important;
  padding: 0 !important;
  margin: 0 auto !important;
}
</style>

<!-- 全局样式：专门用于覆盖Element UI弹窗 -->
<style>
/* 全局样式 - 不使用scoped，以便能够影响body下的元素 */
.el-picker-panel {
  left: 50% !important;
  transform: translateX(-50%) !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  z-index: 3000 !important;
}

/* 隐藏日期选择器箭头 */
.el-picker-panel .popper__arrow {
  display: none !important;
}

/* 优化日期选择器内容区域的padding，增加可视区域 */
.el-picker-panel .el-date-picker__body {
  padding: 10px 8px !important;
}

/* 优化日期表格单元格间距 */
.el-picker-panel .el-date-table th,
.el-picker-panel .el-date-table td {
  padding: 2px !important;
  width: 30px !important;
  height: 30px !important;
}

/* 优化日期选择器头部间距 */
.el-picker-panel .el-date-picker__header {
  margin: 6px 0 !important;
  padding: 0 8px !important;
}

/* 减少日期输入框的内部padding */
.date-picker-item .el-input__inner {
  padding-right: 25px !important;
  font-size: 13px !important;
}

/* 确保月份和年份选择下拉菜单不会被截断 */
.el-picker-panel .el-date-picker__header-label {
  font-size: 14px !important;
  padding: 0 2px !important;
}

/* 移动端确认弹窗优化样式 */
.mobile-confirm-dialog {
  width: 88% !important;
  max-width: 420px !important;
  border-radius: 12px !important;
}
.mobile-confirm-dialog .el-message-box__header {
  padding: 12px 16px !important;
}
.mobile-confirm-dialog .el-message-box__title {
  font-size: 18px !important;
}
.mobile-confirm-dialog .el-message-box__content {
  padding: 12px 16px 0 16px !important;
  font-size: 15px !important;
  line-height: 1.6 !important;
}
.mobile-confirm-dialog .el-message-box__btns {
  padding: 12px 16px 16px !important;
}
.mobile-confirm-dialog .el-button + .el-button {
  margin-left: 12px !important;
}
.mobile-confirm-dialog .el-message-box__status { display: none !important; }
.mobile-confirm-dialog .el-button { padding: 10px 14px !important; }
</style> 